import { forwardRef, useEffect, useImperativeHandle } from "react";
import { DataTable } from "primereact/datatable";
import { useAPIGetCustomer } from "../api/api-get-customer-selector";
import { Column } from "primereact/column";
import CustomerSelectorFilter from "./customer-selector-filter";
import { Paginator } from "primereact/paginator";
import { genTemplateBodyText } from "../../../../components/utils/TableTemplateBodyGenerator";
import { useSelector } from "react-redux";
import useIsMobile from "../../../../hooks/useIsMobile";

const CustomerSelectorList = forwardRef((props, ref) => {
  const { visible, additionalFilter, multiple } = props;
  const employee = useSelector((state) => state.auth.employee);
  const isMobile = useIsMobile();

  const {
    loading,
    listData,
    fetchListData,
    filterValue,
    setFilterValue,
    handleOnClickRefresh,
    handleOnPage,
    paging,
    handleOnUpdateViewRow,
    handleOnDeleteViewRow,
    listSelected,
    setListSelected,
  } = useAPIGetCustomer({ additionalFilter });

  useImperativeHandle(ref, () => ({
    handleOnUpdateViewRow: (row) => handleOnUpdateViewRow(row),
    handleOnDeleteViewRow: (row) => handleOnDeleteViewRow(row),
    getSelectedData: () => listSelected,
  }));

  useEffect(() => {
    if (visible) {
      setListSelected([]);
      fetchListData({ page: paging.page, take: paging.take });
    }
  }, [visible]);

  // template section
  const BcBodyTemplate = (row) => genTemplateBodyText({ row, field: "data_bc.name" });
  const CodeBodyTemplate = (row) => genTemplateBodyText({ row, field: "code" });
  const NameBodyTemplate = (row) => genTemplateBodyText({ row, field: "name" });
  const Phone1BodyTemplate = (row) => genTemplateBodyText({ row, field: "phone_1" });
  const Phone2BodyTemplate = (row) => genTemplateBodyText({ row, field: "phone_2" });
  const AddressBodyTemplate = (row) => genTemplateBodyText({ row, field: "address" });

  const MobileBodyTemplate = (row) => {
    return (
      <div className="col-start-start gap-1">
        {employee?.user_type != 3 && (
          <div className="text-xs text-gray-500">
            <strong>BC:</strong> {row?.data_bc?.name || "-"}
          </div>
        )}
        <div className="text-sm font-semibold text-gray-700">{row.name}</div>
        <div className="text-xs text-gray-500">{row.code}</div>
        <div className="text-xs text-gray-600">
          📞 {row.phone_1 || "-"}{" "}
          {row.phone_2 && <span className="ml-1 text-gray-400">| {row.phone_2}</span>}
        </div>
        <div className="text-xs text-gray-500">{row.address || "-"}</div>
      </div>
    );
  };

  return (
    <div className="col-start-start w-full gap-4">
      {/* filter section */}
      <CustomerSelectorFilter
        filterValue={filterValue}
        setFilterValue={setFilterValue}
        onClickRefresh={handleOnClickRefresh}
      />

      {/* table or card layout */}
      <div className="col-start-start w-full gap-2">
        {!isMobile ? (
          // 🖥️ Desktop Table
          <DataTable
            dataKey="id"
            onSelectionChange={(e) => setListSelected(e.value)}
            selection={listSelected}
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={listData}
          >
            <Column
              selectionMode={multiple ? "multiple" : "single"}
              headerStyle={{ width: "3rem" }}
            ></Column>

            <Column
              field="no"
              header="#"
              align="center"
              className="text-center"
              headerClassName="w-[4rem]"
            />
            {employee?.user_type != 3 && (
              <Column header="BC" field="data_bc.name" body={BcBodyTemplate} />
            )}
            <Column header="Code" field="code" body={CodeBodyTemplate} />
            <Column header="Name" field="name" body={NameBodyTemplate} />
            <Column header="Phone 1" field="phone_1" body={Phone1BodyTemplate} />
            <Column header="Phone 2" field="phone_2" body={Phone2BodyTemplate} />
            <Column header="Address" field="address" body={AddressBodyTemplate} />
          </DataTable>
        ) : (
          // 📱 Mobile Card Layout
          <DataTable
            dataKey="id"
            onSelectionChange={(e) => setListSelected(e.value)}
            selection={listSelected}
            className="w-100"
            loading={loading}
            showGridlines
            stripedRows
            value={listData}
          >
            <Column
              selectionMode={multiple ? "multiple" : "single"}
              headerStyle={{ width: "3rem" }}
              hidden={true}
            ></Column>

            <Column
              field="no"
              header="#"
              align="center"
              className="text-center"
              headerClassName="w-[4rem]"
              hidden={true}
            />
            <Column header="Customer" body={MobileBodyTemplate} />
          </DataTable>
          // <div className="grid grid-cols-1 gap-2 w-full">
          //   {listData?.map((row) => {
          //     const selected = listSelected.some((x) => x.id === row.id);
          //     return (
          //       <div
          //         key={row.id}
          //         onClick={() => {
          //           if (multiple) {
          //             setListSelected((prev) =>
          //               selected ? prev.filter((x) => x.id !== row.id) : [...prev, row]
          //             );
          //           } else {
          //             setListSelected([row]);
          //             console.log('selected single', row);
          //           }
          //         }}
          //         className={`p-3 rounded-xl border transition-all duration-200 shadow-sm cursor-pointer ${selected
          //           ? "bg-blue-50 border-blue-400"
          //           : "bg-white hover:bg-gray-50 border-gray-200"
          //           }`}
          //       >
          //         {employee?.user_type != 3 && (
          //           <div className="text-xs text-gray-500 mb-1">
          //             <strong>BC:</strong> {row?.data_bc?.name || "-"}
          //           </div>
          //         )}
          //         <div className="text-sm font-semibold text-gray-700">{row.name}</div>
          //         <div className="text-xs text-gray-500">{row.code}</div>
          //         <div className="text-xs text-gray-600 mt-1">
          //           📞 {row.phone_1 || "-"}{" "}
          //           {row.phone_2 && <span className="ml-1 text-gray-400">| {row.phone_2}</span>}
          //         </div>
          //         <div className="text-xs text-gray-500 mt-1">{row.address || "-"}</div>
          //       </div>
          //     );
          //   })}
          //   {loading && (
          //     <div className="text-center text-gray-400 py-4 text-sm">Loading...</div>
          //   )}
          //   {!loading && listData?.length === 0 && (
          //     <div className="text-center text-gray-400 py-4 text-sm">
          //       No customers found
          //     </div>
          //   )}
          // </div>
        )}

        {/* paginator */}
        <Paginator
          first={parseInt(paging.take) * (paging.page - 1)}
          rows={parseInt(paging.take)}
          totalRecords={parseInt(paging.total)}
          template={{ layout: "PrevPageLink CurrentPageReport NextPageLink" }}
          onPageChange={handleOnPage}
          pt={{
            prevPageButton: {
              className:
                "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
            },
            nextPageButton: {
              className:
                "bg-red-100 !m-0 !max-w-[22px] !w-[22px] !min-w-[22px] max-h-[22px]",
            },
            current: {
              className: "bg-red-100 !m-0 max-h-[22px] text-sm",
            },
          }}
        />
      </div>
    </div>
  );
});

export default CustomerSelectorList;
